import { useCallback, useRef, useEffect, useState } from "react";

/**
 * Caches integration config fetch results in memory for the session.
 * Prevents redundant API calls when navigating away and back to integrations.
 *
 * Usage:
 *   const cache = useIntegrationConfigsCache();
 *   const data = await cache.fetch('shopify', () => fetchShopifyConfig(api));
 *
 * Each cache entry expires after 10 minutes or can be manually invalidated.
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const cache = new Map<string, CacheEntry<any>>();

export function useIntegrationConfigsCache() {
  const cacheRef = useRef(cache);

  const fetch = useCallback(async <T,>(
    key: string,
    fetcher: () => Promise<T>
  ): Promise<T> => {
    // Check if cached and not expired
    const entry = cacheRef.current.get(key);
    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
      return entry.data;
    }

    // Fetch fresh data
    const data = await fetcher();
    cacheRef.current.set(key, { data, timestamp: Date.now() });
    return data;
  }, []);

  const invalidate = useCallback((key: string) => {
    cacheRef.current.delete(key);
  }, []);

  const invalidateAll = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  return { fetch, invalidate, invalidateAll };
}
